import { FC, useEffect } from "react";
import smoothscroll from "smoothscroll-polyfill";
import { buildPageProperties, getPageSubCat } from "src/lib/event/properties";
import queryString from "query-string";

/**
 * @see https://medium.com/@keeganfamouss/accessibility-on-demand-with-chakra-ui-and-focus-visible-19413b1bc6f9
 */
import "focus-visible/dist/focus-visible";

/**
 * For the font-face :
 * @see https://github.com/gatsbyjs/gatsby/issues/19400#issuecomment-692024937
 */
import { GatsbyBrowser, navigate } from "gatsby";

import "./main.css";

import VisitorContext from "src/providers/VisitorContext";
import useVisitorContext from "src/providers/VisitorContext/useVisitorContext";
import Cookies from "src/components/Cookies";
import { Helmet } from "react-helmet";
import { identify, page } from "src/lib/analytics";
import { extractsGclid, hasChangedLocation } from "src/lib/event";
import { hasPersistentParam } from "src/lib/queryParams";
import { initDatadogRum } from "src/lib/datadog";
import { VisitorContextType } from "src/providers/VisitorContext/types";

// Enable smooth scrolling for all browsers, mainly Safari.
smoothscroll.polyfill();

const previousGclid = null;

export const wrapRootElement: GatsbyBrowser["wrapRootElement"] = ({
  element,
}) => (
  <VisitorContext>
    <Helmet>
      {/* gatsby-plugin-sitemap already puts in <head> the main sitemap of the website. */}
      {/* But the Copilote project is hosted on the same domain, and we should reference here its own sitemap too. */}
      <link
        rel="sitemap"
        type="application/xml"
        href="/blog/sitemap/sitemap-index.xml"
      />
    </Helmet>

    {element}
  </VisitorContext>
);

const PageLayout: FC<{
  href: string;
  path: string;
  search: string;
}> = ({ href, path, search, children }) => {
  const { updateVisitorContext } = useVisitorContext();

  useEffect(() => {
    const newVisitorContext: Partial<VisitorContextType> = {};
    // Save marketing/partnership source for tracking purpose
    // Use the last one visited
    const isVisitingTapfiliateTrackedLandingPage =
      /\/lp\/offre|\/lp\/partenaire|\/lp\/ref/.test(path);

    const isVisitingAffilaeTrackedLandingPage = /(\?|&)ae=\d+/.test(href);

    if (
      isVisitingTapfiliateTrackedLandingPage ||
      isVisitingAffilaeTrackedLandingPage
    ) {
      const newUrl = new URL(window.location.href);

      if (!newUrl.searchParams.has("va")) {
        newUrl.searchParams.append("va", Date.now().toString());

        window.history.replaceState({}, "", newUrl.href);
      }

      // Note that this will not overwrite the affiliateSource/LandingHref
      // if the user reloads the page (or revisits again at a later date)
      // without the affiliate path or query param - e.g. navigating directly to
      // www.shine.fr.

      // there no concept of "affiliateSource" when using Affilae
      newVisitorContext.affiliateSource = isVisitingTapfiliateTrackedLandingPage
        ? path
        : "AFFILAE";
      newVisitorContext.affiliateLandingHref = newUrl.href;
    }

    // For acquisition purposes, as long as the query params include utm, we record the last match
    if (hasPersistentParam(search)) {
      newVisitorContext.search = search;
    }

    updateVisitorContext(newVisitorContext);
  }, [href, path, search, updateVisitorContext]);

  return <Cookies>{children}</Cookies>;
};

export const wrapPageElement: GatsbyBrowser["wrapPageElement"] = ({
  element,
  props: { location, path },
}) => (
  <PageLayout path={path} href={location.href} search={location.search}>
    {element}
  </PageLayout>
);

export const onClientEntry: GatsbyBrowser["onClientEntry"] = async () => {
  initDatadogRum();

  /**
   * Used for the Wistia SDK.
   */
  window.wistiaInitQueue = window.wistiaInitQueue || [];

  if (typeof IntersectionObserver === `undefined`) {
    await import("intersection-observer");
  }

  // If `va` is already in the URL (e.g. from a shared affiliate link), replace it
  // with the current timestamp so attribution reflects this visitor's actual visit.
  const url = new URL(window.location.href);
  if (url.searchParams.has("va")) {
    url.searchParams.set("va", Date.now().toString());
    window.history.replaceState({}, "", url.href);
  }
};

export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = ({
  location,
  prevLocation,
}) => {
  const shouldLogPageView = hasChangedLocation(prevLocation, location);
  const { pathname } = location;

  if (shouldLogPageView) {
    const pageSubCat = getPageSubCat(location);
    const pageSubCatObj = pageSubCat ? { pageSubCat1: pageSubCat } : {};

    // TODO: LATER: This setup is useful for organic AB testing
    // const shouldIncludeExperimentVariant = process.env.GATSBY_EXPERIMENT_VARIANT_NAME !== 'all';
    // const experimentVariantObj = shouldIncludeExperimentVariant
    //   ? { experimentVariant: process.env.GATSBY_EXPERIMENT_VARIANT_NAME }
    //   : {};

    // This setup is useful for paid AB testing
    const isA = pathname.startsWith("/hello");
    const isB = pathname.startsWith("/welcome");
    const shouldIncludeExperimentVariant = isA || isB;
    const experimentVariantObj = shouldIncludeExperimentVariant
      ? { experimentVariant: isA ? "paid-new-hp-a" : "paid-new-hp-b" }
      : {};

    const properties = {
      // This value is logged to analytics tools and can be edited on Netlify > Environment Variables
      ...experimentVariantObj,
      pageEnvironment: process.env.GATSBY_DEPLOY_ENV,
      pageSite: "marketing site",
      ...pageSubCatObj,
      ...buildPageProperties(location),
    };

    /**
      No matter the user consent, we disable fake CAPI for identify because:
      - it does not support identify type
      - it does not provide a UI way to filter them out without noise in `Event Delivery` tab of Segment
    **/

    // if (shouldIncludeExperimentVariant) {
    //   // https://segment.com/docs/connections/destinations/catalog/mixpanel/#people
    //   identify(experimentVariantObj, {
    //     integrations: {
    //       'Facebook Conversions API': false,
    //       'Facebook Conversions API - Marketing Website': false,
    //       'Facebook Pixel Server Side': false
    //     }
    //   });
    // }

    /**
      No matter the user consent, we disable fake CAPI for page views because:
      - it does not support page type
      - it does not provide a UI way to filter them out without noise in `Event Delivery` tab of Segment
    **/
    page("", document.title, properties, {
      integrations: {
        'Facebook Conversions API': false,
        'Facebook Conversions API - Marketing Website': false,
        'Facebook Pixel Server Side': false
      }
    });
  }

  const newGclid = extractsGclid(location);

  if (newGclid && previousGclid !== newGclid) {
    identify({ gclid: newGclid }, {
      integrations: {
        'Facebook Conversions API': false,
        'Facebook Conversions API - Marketing Website': false,
        'Facebook Pixel Server Side': false
      }
    });
  }
};

export const shouldUpdateScroll: GatsbyBrowser["shouldUpdateScroll"] = ({ prevRouterProps, routerProps }) => {
  const isSamePathname = prevRouterProps?.location?.pathname === routerProps?.location?.pathname;
  const isSameSearch = prevRouterProps?.location?.search === routerProps?.location?.search;
  if (isSamePathname && !isSameSearch) {
    return false;
  }

  // For other cases (like a full page change), scroll to top
  return true;
};

export const disableCorePrefetching: GatsbyBrowser["disableCorePrefetching"] =
  () => true;
